/**
 * "Add AGG conform feedback" on a Candidate Requests reply — the Requests-tab sibling of the
 * Archive modal's button from PR #239, fed from the decision log ("the source is the log tab").
 *
 * Reuses `aggFeedbackBlock.ts` unchanged: the same marker span, the same insert-before-the-
 * sign-off placement, the same one-click removal. Its own component because InboundCard and
 * EmailBodyEditor both sit at the 500-line ceiling — and because everything here answers one
 * question: what may this button do to the editor right now?
 *
 * Four states, in order of precedence:
 *   1. block inserted (marker present)          → "Remove AGG feedback"
 *   2. prose present as TEXT but no marker      → disabled "Already in this email". This is the
 *      rewrite case: the model returns plain text, destroying the marker while keeping the words,
 *      and without this check the button would offer to insert a second copy.
 *   3. `blockHtml` null                          → disabled, with the server's reason (Library
 *      template missing / {feedback} token edited away) — never hidden, so the recruiter learns
 *      what to fix, exactly like the Archive modal.
 *   4. otherwise                                 → "Add AGG conform feedback"
 *
 * The warnings render BEFORE any click, so they are read before the decision, not after it.
 */
import { useEffect, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import type { InboundRequest } from '../../types/taInboundRequests';
import { hasFeedbackBlock, insertFeedbackBlock, removeFeedbackBlock } from './aggFeedbackBlock';
import { COLORS } from './format';

export function DecisionFeedbackControl({
  item,
  editorRef,
  german,
  onChanged,
}: {
  item: InboundRequest;
  editorRef: React.RefObject<HTMLDivElement | null>;
  /** The REPLY's language, for the mismatch warning; the block itself follows the prose. */
  german: boolean;
  onChanged: () => void;
}) {
  const info = item.decisionFeedback;
  const proseStart = info?.proseStart || '';
  /**
   * The truth about the button lives in the editor's DOM (the marker span, the prose text), and
   * react-hooks forbids reading a ref during render — so a MutationObserver mirrors the DOM into
   * state. That is not ceremony: it is what keeps the label honest through everything ELSE that
   * edits the editor — a ✨ rewrite that wipes the marker but keeps the words (→ "Already in this
   * email"), an undo that brings the marker back, the recruiter deleting the paragraph by hand.
   * A click-time-only read had the button offering a second copy after exactly those.
   */
  const [{ inserted, alreadyAsText }, setDomState] = useState({ inserted: false, alreadyAsText: false });
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !proseStart) return;
    const read = () => {
      const has = hasFeedbackBlock(editor);
      setDomState({ inserted: has, alreadyAsText: !has && editor.innerText.includes(proseStart) });
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(editor, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [editorRef, proseStart]);

  if (!info) return null;
  const languageMismatch = info.proseLanguage !== (german ? 'de' : 'en');

  const toggle = () => {
    const editor = editorRef.current;
    if (!editor) return;
    if (inserted) removeFeedbackBlock(editor);
    else if (info.blockHtml && insertFeedbackBlock(editor, info.blockHtml)) onChanged();
    else return; // nothing was inserted, so there is nothing to count
    // Mirrors the Archive modal's event; booleans and the decision word only, never the paragraph.
    void logAnalyticsEvent('ta_agg_feedback_toggled', {
      surface: 'requests',
      added: !inserted,
      decision: info.decision ?? 'unknown',
      language_mismatch: languageMismatch,
    });
  };

  return (
    <div className="space-y-2" data-no-toggle>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={alreadyAsText || (!inserted && !info.blockHtml)}
          onClick={toggle}
          title={
            alreadyAsText
              ? 'The feedback text is already in this email'
              : (info.unavailableReason ?? 'Insert the AGG-checked feedback from the decision log')
          }
        >
          {inserted
            ? '− Remove AGG feedback'
            : alreadyAsText
              ? 'Already in this email'
              : '+ Add AGG conform feedback'}
        </Button>
        <span className="text-xs text-gray-500">
          From the decision log
          {info.decidedAt ? ` (${new Date(info.decidedAt).toLocaleDateString('en-GB')})` : ''} — the same
          paragraph the Log page shows.
          {info.inDraft && inserted ? ' It is already in the draft; remove it to answer differently.' : ''}
        </span>
      </div>

      {!inserted && !alreadyAsText && !info.blockHtml && info.unavailableReason && (
        <div className="rounded-lg bg-gray-100 p-2 text-sm text-gray-600">{info.unavailableReason}</div>
      )}

      {/*
        ⚠️ Deliberate reversal, warned: offering feedback for a candidate who ADVANCED reverses the
        generated path's "still in process" refusal, for this manual path only (Sofia, Sep 1 2026).
      */}
      {info.stillInProcess && (
        <div className="rounded-lg p-2 text-sm" style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}>
          <span className="font-semibold">Still in process: </span>
          they advanced at this stage — sharing this feedback now pre-empts a decision nobody has made.
        </div>
      )}

      {languageMismatch && (
        <div className="rounded-lg p-2 text-sm" style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}>
          <span className="font-semibold">Language: </span>
          the stored feedback is in {info.proseLanguage === 'de' ? 'German' : 'English'} and this reply is in{' '}
          {german ? 'German' : 'English'}. It is inserted as approved — do not translate it — so adjust the reply
          around it, or offer a call instead.
        </div>
      )}

      {inserted && !info.inDraft && (
        <div className="rounded-lg p-2 text-sm" style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}>
          <span className="font-semibold">Check the draft around it: </span>
          {item.draftSource === 'feedback'
            ? 'the draft above already contains generated feedback — keep one version, not both.'
            : 'if the draft still promises to come back to them, adjust it — you are answering now.'}
        </div>
      )}
    </div>
  );
}
